import { z } from "zod";
import { Command, WorldSnapshot } from "@radar-sim/schema";

/**
 * Wire protocol between browser clients and the session Durable Object.
 *
 * Versioning. The wire protocol carries its own version separate from the
 * snapshot/scenario schema versions. A bump implies a breaking change in the
 * envelope shape itself; both peers must be on the same major.
 */
export const PROTOCOL_VERSION = 1 as const;

/* ===== Auth + session HTTP types ===== */

export const RequestMagicLink = z.object({
  email: z.string().email(),
});
export type RequestMagicLink = z.infer<typeof RequestMagicLink>;

export const VerifyMagicLink = z.object({
  email: z.string().email(),
  token: z.string().min(8),
});
export type VerifyMagicLink = z.infer<typeof VerifyMagicLink>;

export const SessionCookieClaims = z.object({
  email: z.string().email(),
  /** ISO datetime — the cookie's hard expiry. */
  expiresAt: z.string().datetime({ offset: true }),
  /** Identifier for the issuing instance — rotates on key roll. */
  kid: z.string(),
});
export type SessionCookieClaims = z.infer<typeof SessionCookieClaims>;

export const CreateSessionRequest = z.object({
  scenarioId: z.string().min(1),
  /** Optional human-readable session label. */
  label: z.string().optional(),
});
export type CreateSessionRequest = z.infer<typeof CreateSessionRequest>;

export const CreateSessionResponse = z.object({
  /** 6-character uppercase join code, e.g. "K8R2QP". */
  joinCode: z.string().regex(/^[A-Z0-9]{6}$/),
  /** Stable session id — UUID. */
  sessionId: z.string(),
});
export type CreateSessionResponse = z.infer<typeof CreateSessionResponse>;

export const JoinSessionRequest = z.object({
  joinCode: z.string().regex(/^[A-Z0-9]{6}$/),
});
export type JoinSessionRequest = z.infer<typeof JoinSessionRequest>;

export const JoinSessionResponse = z.object({
  sessionId: z.string(),
  scenarioId: z.string(),
  /**
   * Token to present on the WebSocket upgrade for this session. Bound to the
   * (sessionId, email) tuple; expires on session close.
   */
  wsTicket: z.string(),
  role: z.enum(["instructor", "student"]),
});
export type JoinSessionResponse = z.infer<typeof JoinSessionResponse>;

/* ===== WebSocket envelope types ===== */

export const WSHelloFromServer = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  kind: z.literal("hello"),
  sessionId: z.string(),
  scenarioId: z.string(),
  tickRateHz: z.number().int().positive(),
  role: z.enum(["instructor", "student"]),
  /** Sim seconds at the moment of hello. Lets the client size its buffer. */
  simTimeSeconds: z.number().gte(0),
});
export type WSHelloFromServer = z.infer<typeof WSHelloFromServer>;

export const WSSnapshot = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  kind: z.literal("snapshot"),
  snapshot: WorldSnapshot,
});
export type WSSnapshot = z.infer<typeof WSSnapshot>;

export const WSCommand = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  kind: z.literal("command"),
  /** Client-supplied id; server echoes in the result for correlation. */
  cmdId: z.string(),
  command: Command,
});
export type WSCommand = z.infer<typeof WSCommand>;

export const WSCommandResult = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  kind: z.literal("commandResult"),
  cmdId: z.string(),
  ok: z.boolean(),
  reason: z.string().optional(),
  code: z
    .enum([
      "rejected_by_source",
      "unauthorised",
      "invalid_state",
      "unknown_target",
    ])
    .optional(),
});
export type WSCommandResult = z.infer<typeof WSCommandResult>;

/**
 * Server-broadcast cursor (used by instructor "follow my pointer" flows).
 * Bearing/range from the canonical own ship — every student sees the same
 * cursor in their own PPI display.
 */
export const WSBroadcastCursor = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  kind: z.literal("broadcastCursor"),
  bearingDeg: z.number().gte(0).lt(360),
  rangeNm: z.number().gte(0),
  visible: z.boolean(),
});
export type WSBroadcastCursor = z.infer<typeof WSBroadcastCursor>;

export const WSError = z.object({
  protocolVersion: z.literal(PROTOCOL_VERSION),
  kind: z.literal("error"),
  message: z.string(),
  fatal: z.boolean(),
});
export type WSError = z.infer<typeof WSError>;

export const WSMessageFromServer = z.discriminatedUnion("kind", [
  WSHelloFromServer,
  WSSnapshot,
  WSCommandResult,
  WSBroadcastCursor,
  WSError,
]);
export type WSMessageFromServer = z.infer<typeof WSMessageFromServer>;

export const WSMessageFromClient = z.discriminatedUnion("kind", [
  WSCommand,
  z.object({
    protocolVersion: z.literal(PROTOCOL_VERSION),
    kind: z.literal("setBroadcastCursor"),
    bearingDeg: z.number().gte(0).lt(360),
    rangeNm: z.number().gte(0),
    visible: z.boolean(),
  }),
]);
export type WSMessageFromClient = z.infer<typeof WSMessageFromClient>;

/* ===== Assessment-mode HTTP types ===== */

export const CreateAssessmentSessionRequest = z.object({
  scenarioId: z.string().min(1),
  enrolledStudentEmails: z.array(z.string().email()).optional(),
});
export type CreateAssessmentSessionRequest = z.infer<
  typeof CreateAssessmentSessionRequest
>;

export const CreateAssessmentSessionResponse = z.object({
  assessmentId: z.string(),
  joinCode: z.string().regex(/^[A-Z0-9]{6}$/),
});
export type CreateAssessmentSessionResponse = z.infer<
  typeof CreateAssessmentSessionResponse
>;

export const AssessmentInstanceStatus = z.enum([
  "pending",
  "active",
  "ended",
]);
export type AssessmentInstanceStatus = z.infer<
  typeof AssessmentInstanceStatus
>;

export const AssessmentInstance = z.object({
  studentEmail: z.string().email(),
  instanceSessionId: z.string(),
  status: AssessmentInstanceStatus,
  recordingId: z.string().optional(),
});
export type AssessmentInstance = z.infer<typeof AssessmentInstance>;

export const AssessmentSessionState = z.object({
  assessmentId: z.string(),
  scenarioId: z.string(),
  joinCode: z.string().regex(/^[A-Z0-9]{6}$/),
  instructorEmail: z.string().email(),
  instances: z.array(AssessmentInstance),
});
export type AssessmentSessionState = z.infer<typeof AssessmentSessionState>;

export const JoinAssessmentRequest = z.object({
  joinCode: z.string().regex(/^[A-Z0-9]{6}$/),
});
export type JoinAssessmentRequest = z.infer<typeof JoinAssessmentRequest>;

export const JoinAssessmentResponse = z.object({
  assessmentId: z.string(),
  /** The student's own forked instance session id. */
  instanceSessionId: z.string(),
  scenarioId: z.string(),
});
export type JoinAssessmentResponse = z.infer<typeof JoinAssessmentResponse>;

export const RecordingMetadataDTO = z.object({
  recordingId: z.string(),
  sessionId: z.string(),
  scenarioId: z.string(),
  tickRateHz: z.number().int().positive(),
  capturedAt: z.string().datetime({ offset: true }),
  snapshotCount: z.number().int().gte(0),
});
export type RecordingMetadataDTO = z.infer<typeof RecordingMetadataDTO>;

export const ListRecordingsResponse = z.object({
  recordings: z.array(RecordingMetadataDTO),
});
export type ListRecordingsResponse = z.infer<typeof ListRecordingsResponse>;

/**
 * Generate a 6-character join code. Uses a 26+10 alphabet but skips
 * confusable characters (0/O, 1/I).
 */
const JOIN_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export function generateJoinCode(rand: () => number = Math.random): string {
  let out = "";
  for (let i = 0; i < 6; i++) {
    out += JOIN_ALPHABET[Math.floor(rand() * JOIN_ALPHABET.length)];
  }
  return out;
}

/** Validate a join code shape (does not check existence). */
export function isJoinCodeShape(s: string): boolean {
  return /^[A-Z0-9]{6}$/.test(s);
}
